import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { useQuery } from 'react-query';
import { fetcher } from '../../utils/api';
import { apiBaseURL } from '../../constants/routes';
import { storageSync } from '../../utils/storage';

const formatSize = (bytes) => {
  if (!bytes) return '';
  return bytes >= 1e6 ? `about ${Math.round(bytes / 1e6)} MB` : `about ${Math.max(1, Math.round(bytes / 1e3))} KB`;
};

const filenameFrom = (response, fallback) => {
  const header = response.headers.get('Content-Disposition') || '';
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/);
  return encoded ? decodeURIComponent(encoded[1]) : fallback;
};

// Save the file of a track you own, in the format you choose. The file comes
// named "Artist - Title" and tagged with the track's details and cover art.
const SaveFileDialog = ({ open, onClose, track }) => {
  const [choice, setChoice] = useState('');
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery(
    ['file-options', track?.id],
    () => fetcher(`${apiBaseURL}/music/track/${track.id}/file-options/`),
    { enabled: open && Boolean(track?.id) }
  );
  const formats = data?.formats || [];
  const selected = choice || formats[0]?.format || '';

  const save = async () => {
    setError('');
    setProgress(0);
    try {
      const response = await fetch(`${apiBaseURL}/music/track/${track.id}/file/?type=${selected}`, {
        headers: { Authorization: `Token ${storageSync.getItem('token')}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Couldn't save the file");
      }
      const total = Number(response.headers.get('Content-Length')) || 0;
      const reader = response.body.getReader();
      const chunks = [];
      let received = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        if (total) setProgress(Math.round((received / total) * 100));
      }
      const extension = formats.find((f) => f.format === selected)?.extension || 'mp3';
      const url = URL.createObjectURL(new Blob(chunks, { type: response.headers.get('Content-Type') || '' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filenameFrom(response, `${track.name || 'track'}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      setProgress(null);
      onClose();
    } catch (e) {
      setProgress(null);
      setError(e.message);
    }
  };

  const busy = progress !== null;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Save file</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {track?.name} - named and tagged with its artist, album and cover art.
        </Typography>
        {isLoading && <LinearProgress sx={{ my: 2 }} />}
        <RadioGroup value={selected} onChange={(event) => setChoice(event.target.value)}>
          {formats.map((format) => (
            <FormControlLabel
              key={format.format}
              value={format.format}
              disabled={busy}
              control={<Radio />}
              label={
                <span>
                  {format.label}
                  <Typography component="span" variant="body2" color="text.secondary"> {formatSize(format.size)}</Typography>
                </span>
              }
            />
          ))}
        </RadioGroup>
        {busy && (
          <>
            <LinearProgress variant={progress ? 'determinate' : 'indeterminate'} value={progress || 0} sx={{ mt: 2 }} />
            <Typography variant="caption" color="text.secondary">
              {progress ? `${progress}%` : 'Preparing your file...'}
            </Typography>
          </>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !selected}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveFileDialog;
